// src/modules/app-views/crm/pages/Boards/components/StickyNote.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tag, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import translateX from 'utils/translateX';
import dayjs from 'dayjs';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const StickyNote = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { note, onEdit, onDelete } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const noteId = note._id || note.uid;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: noteId,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const rotation = computeRotation(noteId);

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 999 : 'auto',
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div ref={setNodeRef} style={sortableStyle} {...attributes}>
      <div
        className="sticky-note-card"
        style={{
          background: note.color || '#fff9c4',
          borderRadius: 2,
          padding: '20px 16px 12px',
          minHeight: 150,
          transform: `rotate(${rotation}deg)`,
          boxShadow: '2px 4px 8px rgba(0,0,0,0.12)',
          cursor: 'grab',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          transition: 'box-shadow 0.2s, transform 0.15s',
        }}
        {...listeners}
      >
        {/* Tape decoration */}
        <div
          style={{
            position: 'absolute',
            top: -8,
            left: '50%',
            transform: 'translateX(-50%) rotate(1deg)',
            width: 50,
            height: 20,
            background: 'rgba(255,255,200,0.8)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            borderRadius: 2,
            pointerEvents: 'none',
          }}
        />

        {/* Actions (top right, visible on hover via CSS) */}
        <div className="sticky-note-actions" onPointerDown={e => e.stopPropagation()}>
          <Tooltip title={translateX('edit')}>
            <EditOutlined
              style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)', cursor: 'pointer' }}
              onClick={() => onEdit(note)}
            />
          </Tooltip>
          <Tooltip title={translateX('delete')}>
            <DeleteOutlined
              style={{ fontSize: 13, color: 'rgba(0,0,0,0.45)', cursor: 'pointer' }}
              onClick={() => onDelete(noteId)}
            />
          </Tooltip>
        </div>

        {/* Title */}
        {note.title && (
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              marginBottom: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'rgba(0,0,0,0.85)',
            }}
          >
            {note.title}
          </div>
        )}

        {/* Content */}
        <div
          style={{
            flex: 1,
            fontSize: 13,
            lineHeight: 1.6,
            color: 'rgba(0,0,0,0.65)',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 6,
            WebkitBoxOrient: 'vertical',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {note.content}
        </div>

        {/* Tags + Date */}
        {((note.tags && note.tags.length > 0) || note.createdAt) && (
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 4,
              alignItems: 'center',
            }}
          >
            {(note.tags || []).map(tag => (
              <Tag key={tag} style={{ fontSize: 10, margin: 0, lineHeight: '18px' }}>
                {tag}
              </Tag>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(0,0,0,0.3)' }}>
              {dayjs(note.createdAt).format('DD/MM')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default StickyNote;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function computeRotation(uid) {
  const code = (uid.charCodeAt(0) || 0) + (uid.charCodeAt(1) || 0);
  return (code % 5) - 2; // range: -2 to 2 degrees
}
